import Component from '@glimmer/component';

import pageTitle from 'ember-page-title/helpers/page-title';
import PageSetup from 'bcl/components/page-setup';
import { on } from '@ember/modifier';
import filterSort from 'bcl/helpers/filter-sort';
import displayDate from 'bcl/helpers/display-date';
import { LinkTo } from '@ember/routing';
import eq from 'ember-truth-helpers/helpers/eq';
import { fn } from '@ember/helper';
import orderStatus from 'bcl/helpers/order-status';
import WaitingSpinner from 'bcl/components/waiting-spinner';
import setElement from 'bcl/modifiers/set-element';
import InputMonth from 'bcl/components/input/month.gts';
import { service } from '@ember/service';
import type OrderModel from 'bcl/models/order';
import type DownloadService from 'bcl/services/download';
import type SessionService from 'bcl/services/session';
import type RouterService from '@ember/routing/router-service';
import type ModalService from 'bcl/services/modal';
import type { Store } from '@warp-drive/core';
import { action } from '@ember/object';
import { query } from 'ember-data-resources';
import { tracked } from '@glimmer/tracking';

export interface OrdersIndexSignature {
  // The arguments accepted by the component
  Args: {
    model: OrderModel[];
  };
  // Any blocks yielded by the component
  Blocks: {
    default: [];
  };
  // The element to which `...attributes` is applied in the component template
  Element: null;
}

export default class OrdersIndexComponent extends Component<OrdersIndexSignature> {
  @service declare modal: ModalService;
  @service declare store: Store;
  @service declare session: SessionService;
  @service declare router: RouterService;
  @service declare download: DownloadService;

  constructor(...args: unknown[]) {
    super(...args);
    // Initialize the start and end dates to the current date
    const date = new Date();
    this.endDate = formatDate(date);
    date.setMonth(date.getMonth() - 1);
    this.startDate = formatDate(date);
  }

  @action
  startDownload(id: string): Promise<void> {
    return this.download.order(id);
  }

  @tracked startDate: string;
  @tracked endDate: string;
  @tracked initial = true;
  foundOrders = query<OrderModel>(this, 'order', () => ({
    filter: {
      'accepted_date:gte': this.startDate,
      'accepted_date:lte': this.endDate,
    },
  }));
  @action
  createOrderSpecial() {
    this.session.orderKind = 'special';
    this.session.orderSuplID = '1';

    this.router.transitionTo('auth.orders.new');
  }

  @action
  setStartDate(value: string) {
    this.startDate = value;
  }
  @action
  setEndDate(value: string) {
    this.endDate = value;
  }

  <template>
    {{pageTitle "Bestellungsübersicht"}}
    <PageSetup @title="Alle Bestellungen">
      <div class="px-4 lg:px-8">
        <div class="sm:flex sm:items-center">
          <div class="sm:flex-auto">

          </div>
          <div class="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">

            <button
              {{on "click" this.modal.toggleNewOrder}}
              class="rounded-md bg-green-500 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-400 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-500"
              type="button"
            >Neue Bestellung anlegen</button>
            <button
              {{on "click" this.createOrderSpecial}}
              type="button"
              class="rounded-md bg-green-500 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-400 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-500"
            >Entnahme oder Einlage</button>
          </div>
        </div>
        <div class="mt-8 flow-root">
          <div class="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <h2 class="text-xl font-bold">Offene Bestellungen</h2>
            <div class="inline-block min-w-full py-2 align-middle px-4 lg:px-8">
              <table class="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr>
                    <th
                      scope="col"
                      class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-3"
                    >Lieferant</th>
                    <th
                      scope="col"
                      class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-3"
                    >Status</th>
                    <th
                      scope="col"
                      class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >Datum</th>
                    <th scope="col" class="relative py-3.5 pl-3 pr-4 sm:pr-3">
                      <span class="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody class="bg-white">
                  {{#each
                    (filterSort @model "!accepted_date" "-delivery_date")
                    as |order|
                  }}
                    <tr class="even:bg-gray-50">
                      <td
                        class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-3"
                      >{{order.supplier.name}}</td>
                      <td
                        class="whitespace-nowrap py-4 pl-4 pr-3 text-sm text-gray-500"
                      >{{orderStatus order.status}}</td>
                      <td
                        class="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                      >{{displayDate order.delivery_date}}</td>

                      <td
                        class="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-3"
                      >
                        {{#if (eq order.status "created")}}
                          <LinkTo
                            @route="auth.orders.edit"
                            @model={{order.id}}
                            class="text-indigo-600 hover:text-indigo-900 ml-4"
                          >bearbeiten<span class="sr-only">, ID
                              {{order.id}}</span></LinkTo>
                          <button
                            type="button"
                            {{on "click" (fn this.startDownload order.id)}}
                            class="text-indigo-600 hover:text-indigo-900 ml-4"
                          >herunterladen
                            {{#if this.download.isDownloading}}
                              <WaitingSpinner class="inline ml-2" />
                            {{/if}}
                          </button>
                        {{else if (eq order.status "ordered")}}
                          <LinkTo
                            @route="auth.orders.accept"
                            @model={{order.id}}
                            class="text-indigo-600 hover:text-indigo-900 ml-4"
                          >annehmen<span class="sr-only">, ID
                              {{order.id}}</span></LinkTo>
                          <LinkTo
                            @route="auth.orders.show"
                            @model={{order.id}}
                            class="text-indigo-600 hover:text-indigo-900 ml-4"
                          >anzeigen<span class="sr-only">, ID
                              {{order.id}}</span></LinkTo>
                          <a
                            href="mailto:{{order.supplier.email}}?subject=Bestellung zum {{displayDate
                              order.delivery_date
                            }}&body=Guten Tag,%0D%0Abitte liefern Sie, wie im Angang angegeben.%0D%0A%0D%0AVielen Dank und eine schöne Woche!"
                            class="text-indigo-600 hover:text-indigo-900 ml-4"
                          >
                            Email öffnen
                          </a>
                        {{else if (eq order.status "canceled")}}
                          <LinkTo
                            @route="auth.orders.show"
                            @model={{order.id}}
                            class="text-indigo-600 hover:text-indigo-900 ml-4"
                          >anzeigen<span class="sr-only">, ID
                              {{order.id}}</span></LinkTo>
                        {{else}}
                          Sollte nicht auftreten
                        {{/if}}
                      </td>
                    </tr>
                  {{else}}
                    <tr>
                      <td colspan="4">

                        <h2 class="text-lg block col-span-4 ml-4 mt-2">
                          Es existieren keine offenen Bestellungen.
                        </h2>
                      </td>

                    </tr>

                  {{/each}}

                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div class="mt-8 flow-root">
          <div class="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <h2 class="text-xl font-bold">Abgeschlossene Bestellungen</h2>
            <div class="inline-block min-w-full py-2 align-middle px-4 lg:px-8">
              <div class="flex items-center gap-4 mb-4">
                <label class="block text-sm font-medium text-gray-700">
                  Angenommen zwischen:
                  <InputMonth
                    class="ml-2"
                    @value={{this.startDate}}
                    @onChanged={{this.setStartDate}}
                  />
                </label>
                <label class="block text-sm font-medium text-gray-700">
                  und
                  <InputMonth
                    class="ml-2"
                    @value={{this.endDate}}
                    @onChanged={{this.setEndDate}}
                  />
                </label>
              </div>
              {{#if this.foundOrders.isError}}
                <div class="text-red-500">
                  Fehler beim Laden der Bestellungen:
                  {{this.foundOrders.error}}
                </div>
              {{else if this.foundOrders.isLoading}}
                <div class="text-gray-500">Lade Bestellungen...</div>
              {{/if}}
              {{#if this.foundOrders.isSuccess}}

                <table class="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr>
                      <th
                        scope="col"
                        class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-3"
                      >Lieferant</th>
                      <th
                        scope="col"
                        class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >geplant</th>
                      <th
                        scope="col"
                        class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >Angenommen an</th>
                      <th
                        scope="col"
                        class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      ></th>
                    </tr>
                  </thead>
                  <tbody class="bg-white" {{setElement this.foundOrders.retry}}>
                    {{#each
                      (filterSort
                        this.foundOrders.records
                        "accepted_date"
                        "-delivery_date"
                      )
                      as |order|
                    }}
                      <tr class="even:bg-gray-50">
                        <td
                          class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-3"
                        >{{order.supplier.name}}
                          {{#if (eq order.supplier.additional "special")}}
                            ({{order.note}})
                          {{/if}}</td>
                        <td
                          class="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                        >{{displayDate order.delivery_date}}</td>
                        <td
                          class="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                        >{{displayDate order.accepted_date}}</td>
                        <td
                          class="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                        ><LinkTo
                            @route="auth.orders.show"
                            @model={{order.id}}
                            class="text-indigo-600 hover:text-indigo-900 ml-4"
                          >anzeigen<span class="sr-only">, ID
                              {{order.id}}</span></LinkTo></td>

                      </tr>
                    {{else}}
                      <tr>
                        <td colspan="4">

                          <h2 class="text-lg block col-span-4 ml-4 mt-2">
                            Es existieren keine abgeschlossenen Bestellungen für
                            den ausgewählten Zeitraum.
                          </h2>
                        </td>

                      </tr>
                    {{/each}}

                  </tbody>
                </table>
              {{/if}}
            </div>
          </div>
        </div>
      </div>

    </PageSetup>
  </template>
}

function formatDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
}
